
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";

interface VideoEmbedProps {
  isOpen: boolean;
  onClose: () => void;
  youtubeId?: string;
  vimeoId?: string;
  videoPlatform?: "youtube" | "vimeo";
  title?: string;
}

const VideoEmbed = ({ 
  isOpen, 
  onClose, 
  youtubeId, 
  vimeoId, 
  videoPlatform,
  title = "Bobo & Teedee® Episode" 
}: VideoEmbedProps) => {
  // Determine which video to show based on platform or available IDs
  const getVideoConfig = () => {
    if (videoPlatform === "vimeo" && vimeoId) {
      return { type: "vimeo", id: vimeoId };
    }
    if (videoPlatform === "youtube" && youtubeId) {
      return { type: "youtube", id: youtubeId };
    }
    // Fallback logic - prioritize the platform that has an ID
    if (vimeoId) {
      return { type: "vimeo", id: vimeoId };
    }
    if (youtubeId) {
      return { type: "youtube", id: youtubeId };
    }
    return null;
  };

  const videoConfig = getVideoConfig();

  if (!videoConfig) {
    return null;
  }

  const getEmbedUrl = () => {
    if (videoConfig.type === "youtube") {
      return `https://www.youtube.com/embed/${videoConfig.id}?autoplay=1&rel=0&modestbranding=1`;
    }
    if (videoConfig.type === "vimeo") {
      return `https://player.vimeo.com/video/${videoConfig.id}?autoplay=1&title=0&byline=0&portrait=0`;
    }
    return "";
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full p-0 bg-black">
        <div className="relative">
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="absolute -top-12 right-0 z-50 text-white hover:bg-white/20 rounded-full"
            aria-label="Close video"
          >
            <X className="h-6 w-6" />
          </Button>
          
          <div className="relative w-full pb-[56.25%]">
            <iframe
              src={getEmbedUrl()}
              title={title}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
            />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VideoEmbed;
